import React, { KeyboardEvent, ChangeEvent, useCallback, useMemo, useRef, useState, useEffect } from 'react';

import {
	useClassNames,
} from '@common';

import {
	SearchIcon,
	RemoveIcon,
} from '@icons';

import './Search.scss';
import { IconButton } from '../IconButton';

interface SearchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
	leading?: JSX.Element,
	trailing?: JSX.Element,
	onChangeSearch: (s: string) => void,
}

function Search(props: SearchProps): JSX.Element {
	const {
		className,
		defaultValue,
		onChangeSearch,
		...otherProps
	} = props;

	const
		inputRef = useRef<HTMLInputElement>(null),
		[focused, setFocused] = useState<boolean>(false),
		[searchQuery, setSearchQuery] = useState<string>('');

	useEffect(() => {
		setSearchQuery(`${defaultValue}`);
	}, [defaultValue]);

	const onFocus = useCallback((e?: React.FocusEvent<HTMLInputElement>): void => {
		setTimeout(() => setFocused(true), 200);
		props.onFocus && props.onFocus(e);
	}, [props]);

	const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>): void => {
		setTimeout(() => setFocused(false), 200);
		props.onBlur && props.onBlur(e);
	}, [props]);

	const handleChangeSearchQuery = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
		setSearchQuery(e.target.value);
	}, [setSearchQuery]);

	const handleResetSearchQuery = useCallback((): void => {
		setSearchQuery('');
		onChangeSearch('');
	}, [setSearchQuery, onChangeSearch]);

	const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			onChangeSearch(searchQuery);
		}
	}, [onChangeSearch, searchQuery]);

	const leadingView = useMemo(() => {
		const leading = props.leading || (
			<IconButton
				icon={<SearchIcon />}
				iconSize="20px"
				size="nogap"
			/>
		);

		return leading && (
			<div className="search__leading">
				{leading}
			</div>
		);
	}, [props.leading]);

	const trailingView = useMemo(() => {
		const
			trailing = props.trailing || (
				<IconButton
					icon={<RemoveIcon />}
					iconSize="20px"
					size="nogap"
				/>
			),
			showTrailing = !!trailing && searchQuery;

		return showTrailing && (
			<div className="search__trailing" onClick={handleResetSearchQuery}>
				{trailing}
			</div>
		);
	}, [handleResetSearchQuery, props.trailing, searchQuery]);

	const classes = useClassNames(
		`search ${focused ? '-focused' : ''}`,
		className,
	);

	return (
		<div className={classes}>
			{leadingView}
			<input
				{...otherProps}
				className="search__input"
				onChange={handleChangeSearchQuery}
				onKeyDown={handleKeyDown}
				value={searchQuery}
				onFocus={onFocus}
				onBlur={onBlur}
				ref={inputRef}
				type="search"
			/>
			{trailingView}
		</div>
	);
}

const MemoisedSearch = React.memo(Search);

export {MemoisedSearch as Search};